/**
 * RTree配置选项（默认优化插入/查询速度）
 */
export interface RTreeOptions {
    /** 每个节点最大条目数（建议4-16，默认8，减少分裂次数） */
    maxEntries?: number;
    /** 禁用类型检查（默认true，牺牲安全性换速度） */
    strictMode?: false;
    /** 临时数组池大小（默认20，减少内存分配开销） */
    tempPoolSize?: number;
  }
  
  /**
   * 紧凑节点（Float32Array存储核心属性，减少内存开销）
   * 格式定义：[x, y, width, height, maxX, maxY, isLeaf(0/1), childCount]
   */
  type CompactNode = Float32Array;
  
  /**
   * RTree数据条目（含预计算maxX/maxY，避免重复计算）
   */
  type RTreeEntry<T> = {
    bbox: Float32Array; // 边界矩形：[x, y, width, height]
    data: T;
    maxX: number; // 预计算：bbox[0] + bbox[2]
    maxY: number; // 预计算：bbox[1] + bbox[3]
    __type: 'entry'; // 类型标记，用于类型守卫区分
  };
  
  /**
   * 节点容器（关联紧凑节点与子节点列表）
   * 子节点类型：非叶子节点→NodeContainer，叶子节点→RTreeEntry
   */
  type NodeContainer<T> = {
    node: CompactNode;
    children: (NodeContainer<T> | RTreeEntry<T>)[];
  };
  
  // 紧凑节点索引常量（避免魔法数字，提升可读性）
  const NODE_X = 0;
  const NODE_Y = 1;
  const NODE_WIDTH = 2;
  const NODE_HEIGHT = 3;
  const NODE_MAX_X = 4;
  const NODE_MAX_Y = 5;
  const NODE_IS_LEAF = 6;
  const NODE_CHILD_COUNT = 7;
  
  /**
   * ------------------------------
   * 核心工具：类型守卫（解决类型混淆问题）
   * ------------------------------
   */
  
  /**
   * 判断是否为CompactNode（必须是Float32Array且长度为8）
   */
  function isCompactNode(value: unknown): value is CompactNode {
    return value instanceof Float32Array && value.length === 8;
  }
  
  /**
   * 判断是否为NodeContainer（含node和children属性，且node是CompactNode）
   */
  function isNodeContainer<T>(value: unknown): value is NodeContainer<T> {
    return (
      typeof value === 'object' &&
      value !== null &&
      'node' in value &&
      'children' in value &&
      isCompactNode((value as NodeContainer<T>).node) &&
      Array.isArray((value as NodeContainer<T>).children)
    );
  }
  
  /**
   * 判断是否为RTreeEntry（含__type="entry"标记，且bbox是Float32Array）
   */
  function isRTreeEntry<T>(value: unknown): value is RTreeEntry<T> {
    return (
      typeof value === 'object' &&
      value !== null &&
      '__type' in value &&
      (value as RTreeEntry<T>).__type === 'entry' &&
      'bbox' in value &&
      isCompactNode((value as RTreeEntry<T>).bbox)
    );
  }
  
  /**
   * ------------------------------
   * 边界工具：适配所有类型的边界计算
   * ------------------------------
   */
  
  /**
   * 创建边界矩形（返回标准格式的Float32Array）
   * @param x 左上角X坐标
   * @param y 左上角Y坐标
   * @param width 宽度
   * @param height 高度
   */
  export function createBBox(x: number, y: number, width: number, height: number): Float32Array {
    return new Float32Array([x, y, width, height]);
  }
  
  /**
   * 检查两个边界对象是否相交（支持NodeContainer和RTreeEntry）
   */
  function intersects<T>(a: NodeContainer<T> | RTreeEntry<T>, b: NodeContainer<T> | RTreeEntry<T>): boolean {
    // 提取a的边界信息
    const [aMinX, aMinY, aMaxX, aMaxY] = isNodeContainer(a)
      ? [a.node[NODE_X], a.node[NODE_Y], a.node[NODE_MAX_X], a.node[NODE_MAX_Y]]
      : [a.bbox[NODE_X], a.bbox[NODE_Y], a.maxX, a.maxY];
  
    // 提取b的边界信息
    const [bMinX, bMinY, bMaxX, bMaxY] = isNodeContainer(b)
      ? [b.node[NODE_X], b.node[NODE_Y], b.node[NODE_MAX_X], b.node[NODE_MAX_Y]]
      : [b.bbox[NODE_X], b.bbox[NODE_Y], b.maxX, b.maxY];
  
    // 相交判断（核心逻辑，无类型隐患）
    return aMinX < bMaxX && aMaxX > bMinX && aMinY < bMaxY && aMaxY > bMinY;
  }
  
  /**
   * 检查点是否在边界对象内（支持NodeContainer和RTreeEntry）
   */
  function containsPoint<T>(target: NodeContainer<T> | RTreeEntry<T>, x: number, y: number): boolean {
    const [minX, minY, maxX, maxY] = isNodeContainer(target)
      ? [target.node[NODE_X], target.node[NODE_Y], target.node[NODE_MAX_X], target.node[NODE_MAX_Y]]
      : [target.bbox[NODE_X], target.bbox[NODE_Y], target.maxX, target.maxY];
  
    return x >= minX && x <= maxX && y >= minY && y <= maxY;
  }
  
  /**
   * 合并两个边界对象的边界（返回新边界+预计算maxX/maxY）
   */
  function mergeBounds<T>(a: NodeContainer<T> | RTreeEntry<T>, b: NodeContainer<T> | RTreeEntry<T>): { bbox: Float32Array; maxX: number; maxY: number } {
    const [aMinX, aMinY, aMaxX, aMaxY] = isNodeContainer(a)
      ? [a.node[NODE_X], a.node[NODE_Y], a.node[NODE_MAX_X], a.node[NODE_MAX_Y]]
      : [a.bbox[NODE_X], a.bbox[NODE_Y], a.maxX, a.maxY];
  
    const [bMinX, bMinY, bMaxX, bMaxY] = isNodeContainer(b)
      ? [b.node[NODE_X], b.node[NODE_Y], b.node[NODE_MAX_X], b.node[NODE_MAX_Y]]
      : [b.bbox[NODE_X], b.bbox[NODE_Y], b.maxX, b.maxY];
  
    // 计算合并后的边界
    const minX = Math.min(aMinX, bMinX);
    const minY = Math.min(aMinY, bMinY);
    const maxX = Math.max(aMaxX, bMaxX);
    const maxY = Math.max(aMaxY, bMaxY);
  
    return {
      bbox: new Float32Array([minX, minY, maxX - minX, maxY - minY]),
      maxX,
      maxY
    };
  }
  
  /**
   * ------------------------------
   * RTree核心实现（高性能，无类型错误）
   * ------------------------------
   */
  export class RTree<T> {
    private root: NodeContainer<T>;
    private maxEntries: number;
    private count = 0;
    // 预分配临时数组池（减少动态内存分配）
    private tempPool: Float32Array[];
    // 栈缓存（复用查询/遍历的栈，减少数组创建）
    private stack: NodeContainer<T>[] = [];
  
    /**
     * 构造RTree实例
     * @param options 配置选项
     */
    constructor(options: RTreeOptions = {}) {
      // 初始化配置（默认优化速度）
      this.maxEntries = options.maxEntries || 8;
      if (this.maxEntries < 2) throw new Error('RTree: maxEntries must be ≥ 2');
  
      // 初始化根节点（默认叶子节点）
      const rootNode: CompactNode = new Float32Array(8);
      rootNode[NODE_IS_LEAF] = 1; // 1 = 叶子节点，0 = 非叶子节点
      this.root = { node: rootNode, children: [] };
  
      // 预分配临时数组池（默认20个，避免频繁new）
      const poolSize = options.tempPoolSize || 20;
      this.tempPool = Array.from({ length: poolSize }, () => new Float32Array(4));
    }
  
    /**
     * 从临时池获取数组（复用，减少GC）
     */
    private getTemp(): Float32Array {
      return this.tempPool.pop()!; // 非严格模式下忽略空值检查
    }
  
    /**
     * 归还数组到临时池（不清理内容，减少开销）
     */
    private releaseTemp(arr: Float32Array): void {
      this.tempPool.push(arr);
    }
  
    /**
     * 创建新的节点容器
     * @param isLeaf 是否为叶子节点
     */
    private createNode(isLeaf: boolean): NodeContainer<T> {
      const node: CompactNode = new Float32Array(8);
      node[NODE_IS_LEAF] = isLeaf ? 1 : 0;
      return { node, children: [] };
    }
  
    /**
     * 插入单个数据条目（核心路径：减少判断，优化速度）
     * @param bbox 边界矩形（Float32Array：[x,y,width,height]）
     * @param data 关联数据
     */
    insert(bbox: Float32Array, data: T): void {
      // 预计算maxX/maxY（一次计算，多次使用）
      const maxX = bbox[NODE_X] + bbox[NODE_WIDTH];
      const maxY = bbox[NODE_Y] + bbox[NODE_HEIGHT];
      const entry: RTreeEntry<T> = { bbox, data, maxX, maxY, __type: 'entry' };
  
      // 迭代选择叶子节点（避免递归栈开销）
      let current = this.root;
      while (current.node[NODE_IS_LEAF] !== 1) {
        let bestChild: NodeContainer<T> | null = null;
        let minArea = Infinity;
  
        // 遍历子节点，选择"合并面积最小"的节点（简化计算）
        for (const child of current.children) {
          if (!isNodeContainer(child)) continue; // 非叶子节点跳过（理论不会出现）
  
          // 临时计算合并面积（复用临时数组）
          const temp = this.getTemp();
          temp[NODE_X] = Math.min(child.node[NODE_X], bbox[NODE_X]);
          temp[NODE_Y] = Math.min(child.node[NODE_Y], bbox[NODE_Y]);
          temp[NODE_MAX_X] = Math.max(child.node[NODE_MAX_X], maxX);
          temp[NODE_MAX_Y] = Math.max(child.node[NODE_MAX_Y], maxY);
          const area = (temp[NODE_MAX_X] - temp[NODE_X]) * (temp[NODE_MAX_Y] - temp[NODE_Y]);
          this.releaseTemp(temp);
  
          // 更新最佳子节点
          if (area < minArea) {
            minArea = area;
            bestChild = child;
          }
        }
  
        current = bestChild!; // 非严格模式下忽略空值
      }
  
      // 添加条目到叶子节点，溢出则分裂
      current.children.push(entry);
      this.updateNodeBBox(current);
      let splitNode: NodeContainer<T> | null = current.children.length > this.maxEntries ? this.split(current) : null;
  
      // 处理分裂链（向上传播分裂）
      while (splitNode) {
        if (current === this.root) {
          // 创建新根节点（提升层级）
          const newRoot = this.createNode(false);
          newRoot.children.push(current, splitNode);
          this.updateNodeBBox(newRoot);
          this.root = newRoot;
        }
  
        // 查找父节点并继续处理
        const parent = this.findParent(this.root, current);
        if (!parent) break;
  
        parent.children.push(splitNode);
        this.updateNodeBBox(parent);
  
        current = parent;
        splitNode = current.children.length > this.maxEntries ? this.split(current) : null;
      }
  
      this.count++;
    }
  
    /**
     * 批量插入多个数据条目（比多次调用insert更高效）
     * @param entries 条目数组（{ bbox: Float32Array; data: T }）
     */
    inserts(entries: Array<{ bbox: Float32Array; data: T }>): void {
      if (entries.length === 0) return;
  
      for (const { bbox, data } of entries) {
        // 预计算条目信息
        const maxX = bbox[NODE_X] + bbox[NODE_WIDTH];
        const maxY = bbox[NODE_Y] + bbox[NODE_HEIGHT];
        const entry: RTreeEntry<T> = { bbox, data, maxX, maxY, __type: 'entry' };
  
        // 简化节点选择：直接选择根节点下的第一个叶子节点
        let current = this.root;
        while (current.node[NODE_IS_LEAF] !== 1) {
          const firstChild = current.children[0];
          current = isNodeContainer(firstChild) ? firstChild : this.createNode(true);
        }
  
        // 添加到叶子节点，溢出则分裂
        current.children.push(entry);
        this.updateNodeBBox(current);
  
        if (current.children.length > this.maxEntries) {
          const splitNode = this.split(current);
          // 移除最后一个条目（已移到splitNode）
          current.children.pop();
          this.updateNodeBBox(current);
  
          // 处理根节点分裂
          if (current === this.root) {
            const newRoot = this.createNode(false);
            newRoot.children.push(current, splitNode);
            this.updateNodeBBox(newRoot);
            this.root = newRoot;
          } else {
            const parent = this.findParent(this.root, current);
            if (parent) {
              parent.children.push(splitNode);
              this.updateNodeBBox(parent);
            }
          }
        }
      }
  
      this.count += entries.length;
    }
  
    /**
     * 线性分裂（最快的分裂算法，无复杂计算）
     * @param node 待分裂的节点容器
     * @returns 分裂后的新节点容器
     */
    private split(node: NodeContainer<T>): NodeContainer<T> {
      const newNode = this.createNode(node.node[NODE_IS_LEAF] === 1);
      const splitIndex = (node.children.length >> 1); // 位运算：除以2，更快
  
      // 直接分割子节点（前半保留，后半移到新节点）
      newNode.children = node.children.splice(splitIndex);
  
      // 更新两个节点的边界
      this.updateNodeBBox(node);
      this.updateNodeBBox(newNode);
  
      return newNode;
    }
  
    /**
     * 更新节点容器的边界（直接操作数组，减少开销）
     * @param node 待更新的节点容器
     */
    private updateNodeBBox(node: NodeContainer<T>): void {
      if (node.children.length === 0) {
        node.node.fill(0); // 空节点重置为0
        return;
      }
  
      let minX = Infinity, minY = Infinity;
      let maxX = -Infinity, maxY = -Infinity;
  
      // 遍历子节点，计算最小/最大边界
      for (const child of node.children) {
        const [cMinX, cMinY, cMaxX, cMaxY] = isNodeContainer(child)
          ? [child.node[NODE_X], child.node[NODE_Y], child.node[NODE_MAX_X], child.node[NODE_MAX_Y]]
          : [child.bbox[NODE_X], child.bbox[NODE_Y], child.maxX, child.maxY];
  
        // 更新边界（减少条件判断，直接赋值）
        if (cMinX < minX) minX = cMinX;
        if (cMinY < minY) minY = cMinY;
        if (cMaxX > maxX) maxX = cMaxX;
        if (cMaxY > maxY) maxY = cMaxY;
      }
  
      // 直接更新紧凑节点数组（避免对象属性访问）
      node.node[NODE_X] = minX;
      node.node[NODE_Y] = minY;
      node.node[NODE_WIDTH] = maxX - minX;
      node.node[NODE_HEIGHT] = maxY - minY;
      node.node[NODE_MAX_X] = maxX;
      node.node[NODE_MAX_Y] = maxY;
      node.node[NODE_CHILD_COUNT] = node.children.length;
    }
  
    /**
     * 范围查询（复用栈缓存，减少内存分配）
     * @param bbox 查询边界（Float32Array：[x,y,width,height]）
     * @returns 匹配的数据数组
     */
    query(bbox: Float32Array): T[] {
      const results: T[] = [];
      // 预计算查询边界的maxX/maxY
      const queryEntry: RTreeEntry<never> = {
        bbox,
        data: undefined as never,
        maxX: bbox[NODE_X] + bbox[NODE_WIDTH],
        maxY: bbox[NODE_Y] + bbox[NODE_HEIGHT],
        __type: 'entry'
      };
  
      // 复用栈缓存，避免每次创建新数组
      this.stack.length = 0;
      this.stack.push(this.root);
  
      while (this.stack.length > 0) {
        const node = this.stack.pop()!;
  
        // 快速过滤：节点与查询边界不相交则跳过
        if (!intersects(node, queryEntry)) continue;
  
        // 叶子节点：收集匹配的条目
        if (node.node[NODE_IS_LEAF] === 1) {
          for (const child of node.children) {
            if (isRTreeEntry(child) && intersects(child, queryEntry)) {
              results.push(child.data);
            }
          }
        } else {
          // 非叶子节点：子节点入栈（逆序保持遍历顺序）
          for (let i = node.children.length - 1; i >= 0; i--) {
            const child = node.children[i];
            if (isNodeContainer(child)) this.stack.push(child);
          }
        }
      }
  
      return results;
    }
  
    /**
     * 点查询（快速判断点是否在边界内）
     * @param x 点的X坐标
     * @param y 点的Y坐标
     * @returns 包含该点的所有数据
     */
    queryPoint(x: number, y: number): T[] {
      const results: T[] = [];
  
      // 复用栈缓存
      this.stack.length = 0;
      this.stack.push(this.root);
  
      while (this.stack.length > 0) {
        const node = this.stack.pop()!;
  
        // 快速过滤：点不在节点边界内则跳过
        if (!containsPoint(node, x, y)) continue;
  
        // 叶子节点：收集匹配条目
        if (node.node[NODE_IS_LEAF] === 1) {
          for (const child of node.children) {
            if (isRTreeEntry(child) && containsPoint(child, x, y)) {
              results.push(child.data);
            }
          }
        } else {
          // 非叶子节点：子节点入栈（逆序）
          for (let i = node.children.length - 1; i >= 0; i--) {
            const child = node.children[i];
            if (isNodeContainer(child)) this.stack.push(child);
          }
        }
      }
  
      return results;
    }
  
    /**
     * 查找节点的父节点（仅用于分裂后处理）
     * @param root 根节点
     * @param target 目标子节点
     * @returns 父节点或null
     */
    private findParent(root: NodeContainer<T>, target: NodeContainer<T>): NodeContainer<T> | null {
      this.stack.length = 0;
      this.stack.push(root);
  
      while (this.stack.length > 0) {
        const node = this.stack.pop()!;
  
        // 检查当前节点的子节点是否包含目标
        if (node.children.includes(target)) {
          return node;
        }
  
        // 非叶子节点：继续遍历子节点
        if (node.node[NODE_IS_LEAF] !== 1) {
          for (const child of node.children) {
            if (isNodeContainer(child)) this.stack.push(child);
          }
        }
      }
  
      return null;
    }
  
    /**
     * 获取当前RTree中的条目总数（O(1)操作）
     */
    size(): number {
      return this.count;
    }
  
    /**
     * 清空RTree（快速重置，不释放预分配内存）
     */
    clear(): void {
      // 重置根节点为新叶子节点
      const rootNode: CompactNode = new Float32Array(8);
      rootNode[NODE_IS_LEAF] = 1;
      this.root = { node: rootNode, children: [] };
      this.count = 0;
      // 保留临时池和栈缓存，避免重建开销
    }
  }